/**
 * Senest sete produkter: localStorage + visning på produktside (Section Rendering).
 * Bruges også af søgning (liste af produkt-id'er).
 */
const STORAGE_KEY = 'viewedProducts';
const MAX_PRODUCTS = 12;

/**
 * @typedef {{ id: string, handle: string }} RecentEntry
 */

export class RecentlyViewed {
  /** @returns {RecentEntry[]} */
  static #read() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];

      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed) || parsed.length === 0) return [];

      const first = parsed[0];
      if (typeof first === 'object' && first !== null && 'id' in first) {
        return /** @type {RecentEntry[]} */ (parsed);
      }

      /** @type {string[]} */
      const legacy = parsed;
      return legacy.map((id) => ({ id: String(id), handle: '' }));
    } catch {
      return [];
    }
  }

  /** @param {RecentEntry[]} rows */
  static #write(rows) {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(rows));
  }

  /**
   * @param {string} productId
   * @param {string} handle
   */
  static addProduct(productId, handle) {
    let rows = this.#read();
    rows = rows.filter((row) => String(row.id) !== String(productId));
    rows.unshift({ id: String(productId), handle: String(handle || '') });
    rows = rows.slice(0, MAX_PRODUCTS);
    this.#write(rows);
  }

  static clearProducts() {
    localStorage.removeItem(STORAGE_KEY);
  }

  /** @returns {string[]} */
  static getProducts() {
    return this.#read()
      .map((r) => r.id)
      .filter(Boolean);
  }

  /** @returns {string[]} */
  static getHandles() {
    return this.#read()
      .map((r) => r.handle)
      .filter((h) => typeof h === 'string' && h.length > 0);
  }
}

/**
 * @param {string} normalizedSectionId
 * @param {string} handle
 */
async function fetchProductCardFragment(normalizedSectionId, handle) {
  let rootPath = '/';
  try {
    rootPath = /** @type {{ Theme?: { routes?: { root_url?: string }}} } */ (window).Theme?.routes?.root_url ?? '/';
  } catch {
    rootPath = '/';
  }
  if (!rootPath.endsWith('/')) rootPath = `${rootPath}/`;

  const url = new URL(`${rootPath}products/${encodeURIComponent(handle)}`, window.location.origin);
  url.searchParams.set('section_id', normalizedSectionId);
  url.searchParams.sort();

  const res = await fetch(url.toString(), { headers: { Accept: 'text/html' } });
  if (!res.ok) return null;

  const html = await res.text();
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const sectionEl = doc.getElementById(`shopify-section-${normalizedSectionId}`);
  const item = sectionEl?.querySelector('.resource-list__item');
  if (!item) return null;

  const clone = item.cloneNode(true);
  if (clone instanceof HTMLElement) {
    clone.removeAttribute('id');
    clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  }

  const frag = document.createDocumentFragment();
  frag.append(clone);
  return frag;
}

/**
 * @param {HTMLElement} sectionEl root #shopify-section-...
 */
export async function mountRecentlyViewedProductList(sectionEl) {
  const inner = sectionEl.querySelector('[data-testid="recently-viewed-products"]');
  const grid = sectionEl.querySelector('[data-testid="recently-viewed-grid"]');
  const cardSection = document.querySelector('.recent-view-card-render-root');
  if (!(inner instanceof HTMLElement) || !(grid instanceof HTMLElement) || !cardSection?.id) return;

  const normalizedId = cardSection.id.replace(/^shopify-section-/, '');
  const limit = Number(inner.dataset.limit || MAX_PRODUCTS);
  const currentHandle = inner.dataset.currentHandle ?? '';

  let handles = RecentlyViewed.getHandles().filter((h) => h && h !== currentHandle);

  const cap = Number.isFinite(limit) && limit > 0 ? limit : MAX_PRODUCTS;
  handles = handles.slice(0, cap);

  if (handles.length === 0) {
    sectionEl.hidden = true;
    return;
  }

  sectionEl.hidden = false;
  grid.replaceChildren();

  for (const handle of handles) {
    const frag = await fetchProductCardFragment(normalizedId, handle);
    if (!frag?.firstChild) continue;
    grid.append(frag);
  }
}
